<ng-container *ngrxLet="uniqueAdvicesWithTreatments$ as uniqueAdvicesWithTreatments">
  <agr-page-detail
    [pageTitle]="pageTitle$ | async"
    [isEmpty]="uniqueAdvicesWithTreatments?.length === 0"
    [emptyTitle]="'noTreatments' | translate"
    [emptyDescription]="'noTreatmentsInAdvice' | translate"
    [hasPrimaryAction]="true"
    primaryActionIcon="print"
    [primaryActionText]="'printAdviceReport' | translate"
    (primaryAction)="print()"
    (backAction)="back()"
    [hasSecondaryAction]="true"
    [secondaryActionIcon]="(showCheckboxes$ | async) ? 'check_box' : 'check_box_outline_blank'"
    secondaryActionText="showCheckboxes"
    (secondaryAction)="showCheckboxes()"
    class="default-report"
    [buttonItems]="buttonItems$ | async"
    (buttonAction)="buttonAction($event)"
  >
    <agr-print-page
      [footerText]="
        ((isGBM$ | async) ? 'gbmWarningFooter' : 'fertilizationWarningFooter') | translate
      "
      [relatedPeople]="relatedPeople$ | async"
      [marginLeft]="true"
      [showPageNumber]="false"
    >
      <app-general-info [reportForLabel]="'planningFor'"></app-general-info>

      @if (isGBM$ | async) {
        <div
          class="notification notification-warning print-only avoid-page-break layout--margin-horizontal"
        >
          <mat-icon>warning</mat-icon>
          <strong class="text--warning"> {{ 'gbmWarningTitle' | translate }}</strong
          ><br />
          <span [innerHTML]="'gbmWarningDescription' | translate"></span>
        </div>
      }

      @if (tableConfigTotalsWithAdvice?.dataSource?.filteredData?.length) {
        <div class="has-custom-footers avoid-page-break spacing--margin-half-vertical">
          <h2 class="text--h5 layout--margin-horizontal">
            {{ 'cropFieldsWithAdviceTitle' | translate }}
          </h2>
          <agr-table
            [config]="tableConfigTotalsWithAdvice"
            data-test="cropFieldsWithAdviceTable"
          ></agr-table>
        </div>
      }
      @if (tableConfigTotalsWithoutAdvice?.dataSource?.filteredData?.length) {
        <div class="has-custom-footers avoid-page-break spacing--margin-half-vertical">
          <h2 class="text--h5 layout--margin-horizontal">
            {{ 'cropFieldsWithoutAdviceTitle' | translate }}
          </h2>
          <agr-table
            [config]="tableConfigTotalsWithoutAdvice"
            data-test="cropFieldsWithoutAdviceTable"
          ></agr-table>
        </div>
      }

      <div>
        <br />
        <mat-accordion>
          @for (advice of sortedAdvicesWithTreatments; track advice; let advice_i = $index) {
            <div class="avoid-page-break">
              @if (advice.cropName !== sortedAdvicesWithTreatments[advice_i - 1]?.cropName) {
                <h2 class="text--h5 layout--margin-horizontal">
                  {{ advice.cropName }}
                </h2>
              }
              <mat-expansion-panel data-test="report-advice" class="page-break-auto">
                <mat-expansion-panel-header class="header-title">
                  <mat-panel-title class="flex__child flex__child-1">
                    {{ advice.cropName }}
                    @if (advice.cropVarietyName && advice.cropFieldAdvices.length === 1) {
                      <span>&nbsp;({{ advice.cropVarietyName }})</span>
                    }
                    ,&nbsp;
                    @if (advice.cropFieldAdvices.length === 1) {
                      <span> {{ advice.cropFieldAdvices[0].name }} </span>
                    }
                    @if (advice.cropFieldAdvices.length > 1) {
                      <span>
                        {{ advice.cropFieldAdvices.length }} {{ 'cropFields' | translate }}
                      </span>
                    }
                    &nbsp;({{ advice.totalHectares | agrNumber }}
                    Ha)
                  </mat-panel-title>
                  <mat-panel-description class="flex__child flex__child-1 screen-only">
                    {{ advice.treatments.length }}
                    {{
                      (advice.treatments.length > 1 ? 'treatments' : 'treatment')
                        | translate
                        | lowercase
                    }}
                  </mat-panel-description>
                </mat-expansion-panel-header>
                @if (advice.cropFieldAdvices.length > 1) {
                  <div class="flex flex--row">
                    <div class="flex__child flex__child-1">
                      <div class="item-index-offset spacing--margin-bottom">
                        @for (
                          cropFieldAdvice of advice.cropFieldAdvices;
                          track cropFieldAdvice;
                          let last = $last
                        ) {
                          <span
                            ><span class="text--bold">{{ cropFieldAdvice.name }}</span> ({{
                              cropFieldAdvice.areaInHectares | agrNumber
                            }}
                            Ha)
                            @if (!last) {
                              <span>, </span>
                            }
                          </span>
                        }
                      </div>
                    </div>
                    <div class="flex__child flex__child-1"></div>
                  </div>
                }
                <mat-accordion>
                  @for (treatment of advice.treatments; track treatment; let treatment_i = $index) {
                    <mat-expansion-panel data-test="report-treatment">
                      <mat-expansion-panel-header>
                        <mat-panel-title class="flex__child flex__child-1">
                          {{ treatment.name }} ({{ treatment.objective | translate }})&nbsp;
                          @if (treatment.frequency > 1) {
                            <span
                              >, {{ treatment.frequency }}
                              {{ 'amountOfRepetitions' | translate }}</span
                            >
                          }
                        </mat-panel-title>
                        <mat-panel-description class="flex__child flex__child-1 screen-only">
                          {{ treatment.aidAdvices.length }}
                          {{
                            (treatment.aidAdvices.length > 1 ? 'products' : 'product')
                              | translate
                              | lowercase
                          }}
                        </mat-panel-description>
                      </mat-expansion-panel-header>
                      @if (treatment.shouldUseAreaInHectaresManual) {
                        <div class="notification notification-warning">
                          <mat-icon>warning</mat-icon>
                          <div class="text--bold text--warning">
                            {{ 'shouldUseAreaInHectaresManual' | translate }}
                          </div>
                          {{ treatment.areaInHectaresManual | agrNumber }} Ha
                        </div>
                      }
                      <table
                        mat-table
                        [dataSource]="treatment.aidAdvices"
                        style="width: 100%"
                        [className]="'table-' + (reportType$ | async)"
                      >
                        <ng-container matColumnDef="dosagePerHectare">
                          <th
                            mat-header-cell
                            *matHeaderCellDef
                            class="treatment-header-normal-size"
                          >
                            {{ 'dosagePerHectare' | translate }}
                          </th>
                          <td mat-cell *matCellDef="let aidAdvice">
                            <span class="text--no-wrap">
                              {{ aidAdvice.dosage | agrNumber }}
                              {{ aidAdvice?.cropAid?.unitOfMeasurement?.toLowerCase() }}
                            </span>
                          </td>
                        </ng-container>
                        <ng-container matColumnDef="product">
                          <th
                            mat-header-cell
                            *matHeaderCellDef
                            class="treatment-header-normal-size"
                          >
                            {{ 'productAlt' | translate }}
                          </th>
                          <td mat-cell *matCellDef="let aidAdvice">
                            {{ aidAdvice?.cropAid?.name }}
                          </td>
                        </ng-container>
                        <ng-container matColumnDef="admissionNumbers">
                          <th
                            mat-header-cell
                            *matHeaderCellDef
                            class="treatment-header-normal-size"
                          >
                            {{ 'admissionNumbersShorted' | translate }}
                          </th>
                          <td mat-cell *matCellDef="let aidAdvice">
                            {{
                              getAdmissionNumberString(
                                aidAdvice?.cropAid?.cropProtection?.admissions
                              )
                            }}
                          </td>
                        </ng-container>
                        <ng-container matColumnDef="total">
                          <th
                            mat-header-cell
                            *matHeaderCellDef
                            class="treatment-header-normal-size"
                          >
                            {{ 'total' | translate }}
                          </th>
                          <td mat-cell *matCellDef="let aidAdvice">
                            <span class="text--no-wrap">
                              {{ getTreatmentAmount(advice, treatment, aidAdvice) | agrNumber }}
                              {{ aidAdvice?.cropAid?.unitOfMeasurement?.toLowerCase() }}
                            </span>
                          </td>
                        </ng-container>
                        <ng-container matColumnDef="maxDosage">
                          <th
                            mat-header-cell
                            *matHeaderCellDef
                            class="treatment-header-normal-size"
                          >
                            {{ 'maxDosage' | translate }}
                          </th>
                          <td mat-cell *matCellDef="let aidAdvice">
                            @if (aidAdvice.legalRestriction?.maxDosage) {
                              <span class="text--no-wrap">
                                {{ aidAdvice.legalRestriction?.maxDosage | agrNumber }}
                                {{ aidAdvice?.cropAid?.unitOfMeasurement?.toLowerCase() }}
                              </span>
                            }
                          </td>
                        </ng-container>
                        <ng-container matColumnDef="yearlyDosage">
                          <th
                            mat-header-cell
                            *matHeaderCellDef
                            class="treatment-header-normal-size"
                          >
                            {{ 'yearlyDosage' | translate }}
                          </th>
                          <td mat-cell *matCellDef="let aidAdvice">
                            @if (aidAdvice.legalRestriction?.yearlyDosage) {
                              <span>
                                {{ aidAdvice.legalRestriction?.yearlyDosage | agrNumber }}
                                {{ aidAdvice?.cropAid?.unitOfMeasurement?.toLowerCase() }}
                              </span>
                            }
                          </td>
                        </ng-container>
                        <ng-container matColumnDef="safetyPeriodInDays">
                          <th
                            mat-header-cell
                            *matHeaderCellDef
                            class="treatment-header-normal-size"
                          >
                            {{ 'safetyPeriodInDays' | translate }}
                          </th>
                          <td mat-cell *matCellDef="let aidAdvice">
                            {{ aidAdvice.legalRestriction?.safetyPeriodInDays }}
                          </td>
                        </ng-container>
                        <ng-container matColumnDef="intervalInDays">
                          <th
                            mat-header-cell
                            *matHeaderCellDef
                            class="treatment-header-normal-size"
                          >
                            {{ 'intervalInDays' | translate }}
                          </th>
                          <td mat-cell *matCellDef="let aidAdvice">
                            {{ aidAdvice.legalRestriction?.intervalInDays }}
                          </td>
                        </ng-container>
                        <ng-container matColumnDef="numberOfTimesPerYear">
                          <th
                            mat-header-cell
                            *matHeaderCellDef
                            class="treatment-header-normal-size"
                          >
                            {{ 'numberOfTimesPerYearShorted' | translate }}
                          </th>
                          <td mat-cell *matCellDef="let aidAdvice">
                            {{ aidAdvice.legalRestriction?.numberOfTimesPerYear }}
                          </td>
                        </ng-container>
                        <ng-container matColumnDef="checkboxes">
                          <th
                            mat-header-cell
                            *matHeaderCellDef
                            class="treatment-header-normal-size"
                          >
                            {{ 'checked' | translate }}
                          </th>
                          <td mat-cell *matCellDef="let aidAdvice">
                            <span class="material-icons"> check_box_outline_blank </span>
                          </td>
                        </ng-container>
                        <tr mat-header-row *matHeaderRowDef="aidAdviceColumns$ | async"></tr>
                        <tr mat-row *matRowDef="let row; columns: aidAdviceColumns$ | async"></tr>
                      </table>
                      &nbsp;
                      @if (treatment.moment && treatment.pointOfAttention) {
                        <table style="width: 93%">
                          <tr>
                            <td style="width: 50%">
                              @if (treatment.moment) {
                                <div
                                  class="flex__child flex__child-1 notification notification-info"
                                >
                                  <mat-icon>info</mat-icon>
                                  <div class="text--bold text--info">
                                    {{ 'moment' | translate }}
                                  </div>
                                  {{ treatment.moment }}
                                </div>
                              }
                            </td>
                            <td style="width: 50%">
                              @if (treatment.pointOfAttention) {
                                <div
                                  class="flex__child flex__child-1 notification notification-info"
                                >
                                  <mat-icon>info</mat-icon>
                                  <div class="text--bold text--info">
                                    {{ 'pointOfAttention' | translate }}
                                  </div>
                                  {{ treatment.pointOfAttention }}
                                </div>
                              }
                            </td>
                          </tr>
                        </table>
                      }
                      @if (treatment.moment && !treatment.pointOfAttention) {
                        <div class="flex__child flex__child-1 notification notification-info">
                          <mat-icon>info</mat-icon>
                          <div class="text--bold text--info">{{ 'moment' | translate }}</div>
                          {{ treatment.moment }}
                        </div>
                      }
                      @if (treatment.pointOfAttention && !treatment.moment) {
                        <div class="flex__child flex__child-1 notification notification-info">
                          <mat-icon>info</mat-icon>
                          <div class="text--bold text--info">
                            {{ 'pointOfAttention' | translate }}
                          </div>
                          {{ treatment.pointOfAttention }}
                        </div>
                      }
                    </mat-expansion-panel>
                  }
                </mat-accordion>
              </mat-expansion-panel>
            </div>
          }
        </mat-accordion>
      </div>
    </agr-print-page>
  </agr-page-detail>
</ng-container>
<ng-template #titleColumn let-row="row">
  <div [innerHTML]="row.title"></div>
</ng-template>
<ng-template #aquaticLifeColumn let-row="row">
  <span
    [class]="getBgClass(row.aquaticLife) + ' spacing--margin-left'"
    [innerHTML]="roundNumber(row.aquaticLife, 1)"
  ></span>
</ng-template>
<ng-template #soilLifeColumn let-row="row">
  <span
    [class]="getBgClass(row.soilLife) + ' spacing--margin-left'"
    [innerHTML]="roundNumber(row.soilLife, 1)"
  ></span>
</ng-template>
<ng-template #groundwaterWashoutColumn let-row="row">
  <span
    [class]="getBgClass(row.groundwaterWashout) + ' spacing--margin-left'"
    [innerHTML]="roundNumber(row.groundwaterWashout, 1)"
  ></span>
</ng-template>
<ng-template #hectaresColumn let-row="row">
  <div [innerHTML]="row.hectares" class="spacing--margin-right"></div>
</ng-template>
